"use client";

import BFLButton from "@repo/shared-ui/BFLButton";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  selectLeadStatus,
  selectLoginStatus,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import React, { FC, useCallback, useEffect } from "react";
import { redirectWithUtmParams } from "../../components/Demat/DematUtils";
import { handleSSORedirect } from "../../components/Demat/DematAccount";
import ListWithShowMore from "../../components/News/components/List";
import TrendingStockCard from "./components/TrendingStockCard";
import { useTrendingStocksPlp } from "./hooks/useTrendingStocksPlp";
import type {
  TrendingStocksCardData,
  TrendingStocksDurationFilter,
  TrendingStocksEntityMap,
  TrendingStocksFieldLabels,
  TrendingStocksPrimaryTab,
  TrendingStocksWatchlistConfig,
  TrendingStockTradeAction,
} from "./types";
import NoResultsFound from "../../components/NoResultsFound";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";

interface TrendingStocksPlpUiConfig {
  tabs: {
    stocks: string;
    fo: string;
  };
  durationFilters: Record<TrendingStocksDurationFilter, string>;
  trade: {
    buyText: string;
    sellText: string;
    headerName: string;
    onboardingUrlWeb?: string;
    onboardingUrlApp?: string;
  };
  showMoreText: string;
  fields?: TrendingStocksFieldLabels;
  watchlist?: TrendingStocksWatchlistConfig;
  emptyState?: {
    title?: string;
    description?: string;
    imageSrc?: string;
  };
  apiFailingState?: {
    title?: string;
    description?: string;
    retryButtonText?: string;
    errorImage?: string;
    apiFailureImageAlt?: string;
  };
}

export interface TrendingStocksPlpInteractiveClientProps {
  initialItems: TrendingStocksCardData[];
  initialTotalCount: number;
  initialShowMore: boolean;
  initialTab: TrendingStocksPrimaryTab;
  initialDuration: TrendingStocksDurationFilter;
  initialHasError?: boolean;
  entityMap?: TrendingStocksEntityMap;
  defaultEntity?: string;
  initialExpandedCards?: number;
  uiConfig: TrendingStocksPlpUiConfig;
}

const durationFilterOptions: TrendingStocksDurationFilter[] = [
  "all_calls",
  "short_term",
  "mid_term",
  "long_term",
];

const TrendingStocksPlpInteractiveClient: FC<TrendingStocksPlpInteractiveClientProps> = ({
  initialItems,
  initialTotalCount,
  initialShowMore,
  initialTab,
  initialDuration,
  initialHasError = false,
  entityMap,
  defaultEntity,
  initialExpandedCards,
  uiConfig,
}) => {
  const loginStatus = useAppSelector(selectLoginStatus);
  const leadStatus = useAppSelector(selectLeadStatus);

  const isLoggedIn = loginStatus === "success" || loginStatus === "perpetual";
  const hasBFSLAccount = leadStatus === "ETB";
  const canShowStocksTab = isLoggedIn;
  const canShowFoTab = isLoggedIn && hasBFSLAccount;

  const onboardingUrl = isOpenInApp()
    ? (uiConfig.trade.onboardingUrlApp ?? uiConfig.trade.onboardingUrlWeb ?? "")
    : (uiConfig.trade.onboardingUrlWeb ?? "");

  const {
    activeTab,
    setActiveTab,
    activeDuration,
    setActiveDuration,
    items,
    showMore,
    isLoading,
    isLoadingMore,
    hasError,
    handleShowMore,
    retryCurrentSelection,
  } = useTrendingStocksPlp({
    initialItems,
    initialTotalCount,
    initialShowMore,
    initialTab,
    initialDuration,
    entityMap,
    defaultEntity,
    initialExpandedCards,
  });

  useEffect(() => {
    if (!canShowFoTab && activeTab === "fo") {
      setActiveTab("stocks");
    }
  }, [activeTab, canShowFoTab, setActiveTab]);

  const handleTradeClick = useCallback(
    async (action: TrendingStockTradeAction, stock: TrendingStocksCardData) => {
      if (!isLoggedIn) {
        store.dispatch(setIsCommonSsoModalVisible(true));
        return;
      }

      if (!hasBFSLAccount) {
        if (onboardingUrl) {
          redirectWithUtmParams(onboardingUrl, "false");
        }
        return;
      }

      const exchange = stock.exchange === 2 ? 2 : 0;
      const exchId = stock.exchId || (exchange === 2 ? "2" : "0");
      const nseToken = stock.nseToken || (exchange === 0 ? stock.token : 0);
      const bseToken = stock.bseToken || (exchange === 2 ? stock.token : 0);

      await handleSSORedirect(
        action,
        loginStatus,
        exchId,
        stock.symbol,
        nseToken,
        bseToken,
        uiConfig.trade.headerName,
      );
    },
    [hasBFSLAccount, isLoggedIn, loginStatus, onboardingUrl, uiConfig.trade.headerName],
  );

  const primaryTabs = [
    ...(canShowStocksTab
      ? [{ label: uiConfig.tabs.stocks, value: "stocks" as const }]
      : []),
    ...(canShowFoTab ? [{ label: uiConfig.tabs.fo, value: "fo" as const }] : []),
  ];

  const shouldShowError = (hasError || initialHasError) && items.length === 0;
  const shouldShowEmpty = !isLoading && !shouldShowError && items.length === 0;

  return (
    <section>
      {primaryTabs.length > 0 && (
        <div className="mt-4 pl-[16px]">
          <BFLTabs
            tabs={primaryTabs}
            selectedTab={activeTab}
            onTabChange={(value: TrendingStocksPrimaryTab) => setActiveTab(value)}
            className="border-b border-[#F2F2F2]"
            tabClassName="pb-2"
            indicatorClassName="bg-[#FF6700] h-[2px] md:h-[2px] md:bottom-0"
            paragraphClassName="text-[12px] md:text-[16px] font-medium"
            gapClassName="gap-6"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 py-[16px] pl-[16px] shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
        {durationFilterOptions.map((filter) => {
          const isActive = activeDuration === filter;
          return (
            <BFLButton
              key={filter}
              onClick={() => setActiveDuration(filter)}
              className={`flex items-center justify-center rounded-[8px] border px-2 py-[5px] md:p-2 text-[14px] font-normal leading-[16px] ${
                isActive
                  ? "border-[#002953] bg-[#002953] text-white"
                  : "border-[#8C9CAD] bg-white text-[#002953]"
              }`}
            >
              {uiConfig.durationFilters[filter]}
            </BFLButton>
          );
        })}
      </div>

      {isLoading && items.length === 0 && (
        <div className="mt-3 md:mt-4 pl-4 pr-4 md:pr-0 flex flex-col gap-3">
          {[1, 2, 3].map((row) => (
            <BFLSkeleton
              key={`trending-card-skeleton-${row}`}
              height={130}
              classes="rounded-[6px]"
            />
          ))}
        </div>
      )}

      {shouldShowError && (
        <BFLApiFailure
          error={uiConfig.apiFailingState?.title ?? "Unable to fetch news"}
          errorDescription={
            uiConfig.apiFailingState?.description ??
            "There was an issue fetching trending stocks for your portfolio. Please try again later."
          }
          retryButtonText={uiConfig.apiFailingState?.retryButtonText ?? "Retry"}
          imageSrc={uiConfig.apiFailingState?.errorImage}
          imageAlt={uiConfig.apiFailingState?.apiFailureImageAlt ?? "Error"}
          onRetryClick={retryCurrentSelection}
        />
      )}

      {shouldShowEmpty && (
        <div className="px-4 py-8">
          <NoResultsFound
            title={uiConfig.emptyState?.title ?? uiConfig.fields?.noResultsTitle}
            description={
              uiConfig.emptyState?.description ?? uiConfig.fields?.noResultsDescription
            }
            aemImages={
              uiConfig.emptyState?.imageSrc
                ? { noResultsImage: uiConfig.emptyState.imageSrc }
                : undefined
            }
          />
        </div>
      )}

      {!shouldShowError && items.length > 0 && (
        <ListWithShowMore
          showMore={showMore}
          onShowMore={() => {
            if (!isLoadingMore) {
              handleShowMore();
            }
          }}
          containerClassName="mb-8"
          buttonContainerClassName="px-0 pt-3 max-w-[716px]!"
          showMoreText={isLoadingMore ? "Loading..." : uiConfig.showMoreText}
        >
          <div className="mt-3 md:mt-4 pl-4 pr-4 md:pr-0 flex flex-col gap-3">
            {items.map((stock, index) => (
              <TrendingStockCard
                key={`${stock.id}-${stock.symbol}-${stock.exchId}-${stock.token}-${index}`}
                stock={stock}
                fieldLabels={uiConfig.fields}
                watchlist={uiConfig.watchlist}
                buyText={uiConfig.trade.buyText}
                sellText={uiConfig.trade.sellText}
                onTradeClick={handleTradeClick}
              />
            ))}
          </div>
        </ListWithShowMore>
      )}
    </section>
  );
};

export default TrendingStocksPlpInteractiveClient;
